import React, { useState, useEffect } from "react";
import { useTheme } from "@mui/material/styles";
import { useGetWeekWiseDefaultMutation, useGetDateRangeDataMutation } from "state/api";
import { useNavigate } from "react-router-dom";

import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Button,
  FormControl,
  Select,
  MenuItem,
  Pagination,
  RadioGroup,
  FormControlLabel,
  Radio,
  TextField
} from "@mui/material";

import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

// Columns for display
const columns = [
  { key: "TeamName",         label: "Team Name"            },
  { key: "Headqrt",          label: "Headquarter"          },
  { key: "Name",             label: "MR Name"              },
  { key: "EmpNo",            label: "Emp No"               },
  { key: "Product",          label: "Product"              },
  { key: "TotalPrescription",label: "Total Rx"             },
  { key: "ScoreWithImage",   label: "Rx Score (With Image)"},
  { key: "ScoreNoImage",     label: "Rx Score (No Image)"  },
];

const DailyWise = () => {



  const theme = useTheme();
  const navigate = useNavigate();

  const [getDayWiseDataTest] = useGetWeekWiseDefaultMutation();
  const [getDateRangeData] = useGetDateRangeDataMutation();

  const [data, setData] = useState([]);
  const [rawData, setRawData] = useState([]);
  const [filtered, setFiltered] = useState([]);

  // Filters
  const [teamFilter, setTeamFilter] = useState("");
  const [productFilter, setProductFilter] = useState("");
  const [employeeFilter, setEmployeeFilter] = useState("");

  // Pagination
  const [page, setPage] = useState(1);
  const rowsPerPage = 10;


  const now = new Date();
  const today = now.toISOString().split("T")[0]


  // Mode: week or date range
  const [mode, setMode] = useState("week");
  const [FromDate, setFromDate] = useState("");
  const [ToDate, setToDate] = useState(today);

  // Format date dd-mm-yyyy with GMT+5:30
  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    const gmtOffsetMs = 5.5 * 60 * 60 * 1000; // GMT+5:30
    const localDate = new Date(date.getTime() + gmtOffsetMs);
    const day = String(localDate.getDate()).padStart(2, "0");
    const month = String(localDate.getMonth() + 1).padStart(2, "0");
    const year = localDate.getFullYear();
    return `${day}-${month}-${year}`;
  };

  // Process API rows: format date and store raw
  const processData = (rows) => {
    const formattedRows = rows.map((row) => ({
      ...row,
      SUBDATE: formatDate(row.SUBDATE)
    }));

    setRawData(formattedRows); // all rows for export
    setData(formattedRows);
    setFiltered(formattedRows);
  };

  // Fetch week data
  const fetchWeekData = async () => {
    // const now = new Date();
    // const month = now.getMonth() + 1;
    // const year = now.getFullYear();

    const response = await getDayWiseDataTest();
    if (response?.data?.success) processData(response.data.data);
  };

  // Fetch date range data
  const fetchDateRangeData = async () => {
    if (!FromDate || !ToDate) return alert("Select both start & end dates");
    const response = await getDateRangeData({ FromDate, ToDate });
    if (response?.data?.success) processData(response.data.data);
  };

  // Group by TeamName & EmpNo and sum Prescription/Score
  const getGroupedData = (rows, displayOnlyStatus1 = true) => {
    const filteredRows = displayOnlyStatus1 ? rows.filter(r => r.STATUS === 1) : rows;
    const grouped = {};

    filteredRows.forEach(r => {
      const key = `${r.TeamName}-${r.EmpNo}`;
      if (!grouped[key]) {
        grouped[key] = {
          TeamName: r.TeamName,
          Headqrt: r.Headqrt,
          Name: r.Name,
          EmpNo: r.EmpNo,
          Product: r.product,
          TotalPrescription: 0,
          ScoreWithImage: 0,
          ScoreNoImage: 0,
        };
      }
      grouped[key].TotalPrescription += r.NumOfPrescriptionInWeek;
      const hasImage = r.prescriptionFilePath !== null && r.prescriptionFilePath !== undefined && r.prescriptionFilePath !== '';
      if (hasImage) {
        grouped[key].ScoreWithImage += r.Score;
      } else {
        grouped[key].ScoreNoImage += r.Score;
      }
    });

    return Object.values(grouped);
  };

  // Apply filters
  useEffect(() => {
    let result = [...data];
    if (teamFilter) result = result.filter(r => r.TeamName === teamFilter);
    if (productFilter) result = result.filter(r => r.product === productFilter);
    if (employeeFilter) result = result.filter(r => r.Name === employeeFilter);
    setFiltered(result);
    setPage(1);
    console.log(result)
  }, [teamFilter, employeeFilter, productFilter, data]);

  // Paginated display
  const paginatedData = getGroupedData(filtered).slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage
  );

  // Export Excel with all STATUS
  const exportToExcel = () => {
    // Apply filters but include all STATUS
    let excelRows = [...rawData];
    if (teamFilter) excelRows = excelRows.filter(r => r.TeamName === teamFilter);
    if (productFilter) excelRows = excelRows.filter(r => r.product === productFilter);
    if (employeeFilter) excelRows = excelRows.filter(r => r.Name === employeeFilter);
    if (mode === "dateRange" && FromDate && ToDate) {
      excelRows = excelRows.filter(r => r.SUBDATE >= formatDate(FromDate) && r.SUBDATE <= formatDate(ToDate));
    }

    const ws = XLSX.utils.json_to_sheet(excelRows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "RawData");
    const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    saveAs(new Blob([excelBuffer]), "DailyWise_RawData.xlsx");
  };

  useEffect(() => {
    if (mode == 'week') {
      fetchWeekData();

    }
  }, [mode]);

  return (
    <Box p={2}>
      <Button variant="outlined" sx={{ mb: 2 }} onClick={() => navigate("/Admin")}>
        ← Back to Admin
      </Button>

      <Typography variant="h4" sx={{ mb: 3 }}>RxScore</Typography>

      {/* Filters */}
      <Box sx={{ display: "flex", gap: 1, mb: 2, flexWrap: "wrap", alignItems: "center" }}>
        <FormControl sx={{ minWidth: 150 }}>
          <Select value={teamFilter} displayEmpty onChange={(e) => setTeamFilter(e.target.value)}>
            <MenuItem value="">All Teams</MenuItem>
            {[...new Set(data.map(d => d.TeamName))].map(team => (
              <MenuItem key={team} value={team}>{team}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl sx={{ minWidth: 150 }}>
          <Select value={employeeFilter} displayEmpty onChange={(e) => setEmployeeFilter(e.target.value)}>
            <MenuItem value="">All Employees</MenuItem>
            {[...new Set(data.map(d => d.Name))].map(e => (
              <MenuItem key={e} value={e}>{e}</MenuItem>
            ))}
          </Select>
        </FormControl>


        <FormControl sx={{ minWidth: 150 }}>
          <Select value={productFilter} displayEmpty onChange={(e) => setProductFilter(e.target.value)}>
            <MenuItem value="">All Products</MenuItem>
            {[...new Set(data.map(d => d.product))].map(p => (
              <MenuItem key={p} value={p}>{p}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <RadioGroup row value={mode} onChange={(e) => setMode(e.target.value)}>
          <FormControlLabel value="week" control={<Radio />} label="Week" />
          <FormControlLabel value="dateRange" control={<Radio />} label="Date Range" />
        </RadioGroup>

        {mode === "dateRange" && (
          <>
            <TextField
              label="Start Date"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={FromDate}
              onChange={(e) => setFromDate(e.target.value)}
            />
            <TextField
              label="End Date"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={ToDate}
              onChange={(e) => setToDate(e.target.value)}
            />
            <Button variant="contained" onClick={fetchDateRangeData}>Submit</Button>
          </>
        )}

        <Box sx={{ ml: "auto" }}>
          <Button variant="contained" color="success" onClick={exportToExcel}>
            Export Raw Excel
          </Button>
        </Box>
      </Box>

      {/* Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              {columns.map(c => <TableCell key={c.key}>{c.label}</TableCell>)}
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedData.map((row, i) => (
              <TableRow key={i}>
                {columns.map(c => <TableCell key={c.key}>{row[c.key]}</TableCell>)}
              </TableRow>
            ))}
            {paginatedData.length === 0 && (
              <TableRow>
                <TableCell colSpan={columns.length} align="center">No data found</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
        <Pagination
          count={Math.ceil(getGroupedData(filtered).length / rowsPerPage)}
          page={page}
          onChange={(e, val) => setPage(val)}
        />
      </Box>
    </Box>
  );
};

export default DailyWise;
